import { Button, Text } from "@zapier/design-system";
import type { SearchableAssetType } from "../../schema";
import styles from "./GlobalSearchErrorNotice.module.css";

export interface AssetSearchErrorMessages {
  /** An empty list indicates that the whole asset-search request failed. */
  formatMessage: (failedAssetTypes: readonly SearchableAssetType[]) => string;
  retryLabel: string;
}

interface Props {
  messages?: AssetSearchErrorMessages;
  failedAssetTypes: readonly SearchableAssetType[];
  isRetrying: boolean;
  retry: () => void;
}

const productNames = {
  zap: "Zaps",
  nextGenZap: "next gen Zaps",
  table: "tables",
  interface: "forms",
  canvas: "canvases",
  agent: "agents",
  chatbot: "chatbots",
  folder: "folders",
} satisfies Record<SearchableAssetType, string>;

const formatter = new Intl.ListFormat("en", { type: "conjunction" });

function getDefaultMessage(failedAssetTypes: readonly SearchableAssetType[]) {
  const names = failedAssetTypes.map((type) => productNames[type]);
  return names.length > 0
    ? `There was a problem searching ${formatter.format(names)}. Try again.`
    : "Your Zaps, tables, and other items couldn’t be searched. Try again.";
}

export function GlobalSearchErrorNotice({
  failedAssetTypes,
  isRetrying,
  retry,
  messages,
}: Props) {
  const message = messages
    ? messages.formatMessage(failedAssetTypes)
    : getDefaultMessage(failedAssetTypes);

  return (
    <>
      <Text>{message}</Text>
      <div className={styles.actions}>
        <Button
          variant="secondary"
          size="small"
          isDisabled={isRetrying}
          onPress={retry}
        >
          {messages?.retryLabel ?? "Retry"}
        </Button>
      </div>
    </>
  );
}
